<script lang="ts">
	import debounce from 'just-debounce-it';
	import { InfoIcon } from '../../../../../assets/icons/InfoIcon';
	import { RangeSlider } from '../../../../../components/RangeSlider';
	import type { RangeSliderValue } from '../../../../../components/RangeSlider/types';
	import { Tooltip } from '../../../../../components/Tooltip';
	import { Txt } from '../../../../../components/Txt';
	import { filterStores } from '../../../state';
	import {
		FilterSliderValuesLabelVariant,
		type FilterSlider,
		type FilterDrawerState
	} from '../../../types';
	import { getFilterCollectionValue } from '../../../utils/getFilterCollectionValue/getFilterCollectionValue';
	import { setFilterCollectionValue } from '../../../utils/setFilterCollectionValue/setFilterCollectionValue';

	interface Props {
		filterCollectionId: string;
		filterSlider: FilterSlider;
	}

	let { filterCollectionId, filterSlider }: Props = $props();

	const { filterStore } = filterStores;

	const handleChange = debounce((val: RangeSliderValue) => {
		setFilterCollectionValue({
			filterCollectionId,
			value: val
		});
	}, 200);

	let value = $derived(
		getFilterCollectionValue<RangeSliderValue>({
			filterCollectionId,
			filter: $filterStore as FilterDrawerState
		})
	);

	let label = $derived(filterSlider.label || '');
	let leftValue = $derived(value[0]);
	let rightValue = $derived(value[1]);

	let valueMatches = $derived(leftValue === rightValue);
	let dataCyPrefix = $derived(`filter-drawer-range-slider-id-${filterCollectionId}`);
</script>

<div>
	{#if filterSlider.values_label_variant === FilterSliderValuesLabelVariant.FullWidth}
		<div class="mb-2 flex items-center justify-between">
			<span class="bg-gray-0 flex items-center gap-1.5 rounded border px-4 py-1">
				<Txt class="text-gray-500">{leftValue}</Txt>
			</span>

			<div class="bg-gray-0 rounded border px-4 py-1">
				<Txt class="text-gray-500">{rightValue}</Txt>
			</div>
		</div>
	{:else}
		<div class="mb-2 flex items-center justify-between">
			<span class="flex items-center gap-1.5">
				<Txt variant="label3" dataCy={`${dataCyPrefix}-description`}>
					{filterSlider.description}
				</Txt>
				{#if filterSlider.tooltip_description}
					<Tooltip dataCy={dataCyPrefix} content={filterSlider.tooltip_description}>
						<InfoIcon class="h-[1rem] w-[1rem]" color="gray-900" />
					</Tooltip>
				{/if}
			</span>
			<Txt variant="label3" component="span" dataCy={`${dataCyPrefix}-label`}>
				{leftValue}

				{#if !valueMatches}
					{`- ${rightValue}`}
				{/if}

				{label?.toLowerCase()}
			</Txt>
		</div>
	{/if}

	<RangeSlider
		class="p-1"
		min={filterSlider.min || 0}
		max={filterSlider.max || 0}
		{value}
		onChange={handleChange}
		--sliderTrackColor={filterSlider.hexcode}
		dataCy={dataCyPrefix}
	/>
</div>
